import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { SourceStatusLevel } from 'webapp/enums/enums';

import joinClasses from 'webapp/helpers/join-classes';
import visibleCheckItem from 'webapp/modifiers/visible-check-item';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import DataManagerConnectedTableRowDatasetActionsComponent from 'webapp/components/data-manager/connected/table-row/dataset-actions';
import DataManagerConnectedTableRowStatusComponent from 'webapp/components/data-manager/connected/table-row/status';
import DataManagerConnectedTableRowSyncFrequencyComponent from 'webapp/components/data-manager/connected/table-row/sync-frequency';
import DummyCellComponent from 'webapp/components/dummy-cell';
import EditableModelPropertyComponent from 'webapp/components/editable-model-property';
import FlexibleToggleCellComponent from 'webapp/components/flexible-toggle-cell';
import VerificationBadgeComponent from 'webapp/components/verification/badge';

import type Source from 'webapp/models-custom/source';
import { DataSourceSyncStatus, DataSourceSyncType } from 'webapp/models/source';

interface Args {
	model: Source;
}

export default class DataManagerConnectedTableRowDatasetComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;
	@service('connect') declare connectService: Service.Connect;

	@tracked isVisibleOnScreen = false;
	@tracked showAll = false;
	@tracked isHovered = false;

	get isHoverActive(): boolean {
		return this.isHovered;
	}

	get model(): Source {
		return this.args.model;
	}

	get metricsText(): string {
		const count = this.model.metrics.length;

		if (count === 0) {
			return 'New metric';
		} else {
			return `${count} ${count === 1 ? 'metric' : 'metrics'}`;
		}
	}

	get hasMetrics(): boolean {
		return this.model.metrics.length > 0;
	}

	get show15MinutesSync(): boolean {
		return (
			this.billingService.isPayingCustomer &&
			this.model.statusLevel === SourceStatusLevel.ACTIVE &&
			!this.model.isPremiumDataSync &&
			this.model.sourceType.show15MinSyncLabelPromo
		);
	}

	get isTypeIngest(): boolean {
		return this.model.isTypeIngest;
	}

	get showSetupOption(): boolean {
		return !this.isDatasetInError && !this.isTypeIngest;
	}

	get isDatasetInError(): boolean {
		return this.model.isDatasetInError;
	}

	get isInitialSync(): boolean {
		return (
			this.model.lastSync?.syncStatus === DataSourceSyncStatus.IN_PROGRESS &&
			this.model.lastSync?.syncType === DataSourceSyncType.INITIAL_SYNC
		);
	}

	get datasetEntityName(): string {
		return this.model.datasetsMetadata?.entity || '';
	}

	get datasetColumnsCount(): string {
		const columnsCount = this.model.datasetsMetadata?.columnsCount;
		if (columnsCount && columnsCount > 0) {
			const separator = this.datasetEntityName.length > 0 ? '·' : '';
			return `${separator} ${columnsCount.toString()} columns`;
		}

		return '';
	}

	get canCreateMetric(): boolean {
		return this.model.lastSync !== undefined && !this.isInitialSync;
	}

	@action
	handleMouseEnter(): void {
		this.isHovered = true;
	}

	@action
	handleMouseLeave(): void {
		this.isHovered = false;
	}

	@action
	handleMetricCreate(): void {
		this.mmCustomQueriesService.openCreateNewPopup(
			'',
			() => {
				// TODO: Datasets - this count has to be updated via pusher in ember? - this is not react based
			},
			this.model?.numericIdForSure,
		);
	}

	@action
	handleDatasetSetup(): void {
		this.connectService.editDatasetSetup(this.model.sourceType.key, {
			sourceId: Number(this.model.id),
		});
	}

	@action
	visibleChanged(isVisible: boolean): void {
		this.isVisibleOnScreen = isVisible;
	}

	@action
	toggleShowAll() {
		this.showAll = !this.showAll;
	}
	<template>
		{{#if this.isVisibleOnScreen}}

			<tr
				class={{joinClasses (array "table-row" (if this.showAll "all-shown") "indented")}}
				data-id={{this.model.id}}
				data-ds_type={{this.model.sourceType.key}}
				{{on "mouseenter" this.handleMouseEnter}}
				{{on "mouseleave" this.handleMouseLeave}}
			>
				<td class="c0 no-grow first">
					<div class="content-start">
						<CommonUiIconComponent @name="ArrowDownRight" @size="md" />
					</div>
				</td>
				<td class="c1">
					<div class="flex">
						<EditableModelPropertyComponent
							@model={{this.model}}
							@editableProperty="title"
							@canEdit={{false}}
						/>
						<VerificationBadgeComponent
							@verification={{this.model.verificationInfo}}
							@size="sm"
						/>
					</div>

					<small class="dataset-info">
						{{#if this.showSetupOption}}
							<CommonUiIconComponent @name="Table" @size="sm" />
							{{this.datasetEntityName}}
							{{this.datasetColumnsCount}}
						{{/if}}
					</small>
				</td>

				<td class="created prio-low" data-label="Created: ">
					{{{this.model.createdText}}}
				</td>

				<td class="prio-low" data-label="Permissions: "></td>

				<td class="prio-medium" data-label="Sync: ">
					<DataManagerConnectedTableRowSyncFrequencyComponent
						@dataSource={{this.model}}
					/>
				</td>

				<td class="prio-medium" data-label="Status: ">
					<DataManagerConnectedTableRowStatusComponent @dataSource={{this.model}} />
				</td>

				<td class="prio-medium" data-label="Metrics: ">
					{{#if this.accountService.isDemoAccount}}
						{{this.metricsText}}
					{{else}}
						{{#unless this.isDatasetInError}}
							{{#if this.hasMetrics}}
								<LinkTo
									class="metrics blue-link"
									@route="metric-library.source.specific"
									@models={{array this.model.sourceType.key this.model.id}}
								>
									{{this.metricsText}}
								</LinkTo>
							{{else}}
								{{#if this.canCreateMetric}}
									<a
										class="action-link"
										role="button"
										{{on "click" this.handleMetricCreate}}
									>
										<CommonUiIconComponent @name="Plus" @size="xs" />
										{{this.metricsText}}
									</a>
								{{/if}}
							{{/if}}
						{{/unless}}
					{{/if}}
				</td>

				<td class="c6 button-ct last">
					<div class="content-end">
						<DataManagerConnectedTableRowDatasetActionsComponent
							@dataSource={{this.model}}
							@isActive={{this.isHoverActive}}
						/>
					</div>
				</td>

				<FlexibleToggleCellComponent
					@showAll={{this.showAll}}
					@toggleShowAll={{this.toggleShowAll}}
				/>

			</tr>

		{{else}}

			<tr
				class="table-row"
				data-id={{this.model.id}}
				{{visibleCheckItem this.visibleChanged}}
			>
				<DummyCellComponent />
			</tr>

		{{/if}}
	</template>
}
